import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { calculateValueScore } from '../lib/calculations';
import AppLayout from '../components/layout/AppLayout';
import type { ProductDetails } from '../types/strain';
import SearchableSelect from '../components/forms/SearchableSelect';

export default function EditSession() {
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const navigate = useNavigate();

    // Loading & Error state
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Available data
    const [products, setProducts] = useState<ProductDetails[]>([]);

    // Form state
    const [productId, setProductId] = useState('');
    const [totalPackagePrice, setTotalPackagePrice] = useState(0);
    const [totalPackageWeight, setTotalPackageWeight] = useState(0);
    const [dosageAmount, setDosageAmount] = useState(0);
    const [dosageUnit, setDosageUnit] = useState('g');
    const [timeOfDay, setTimeOfDay] = useState('Evening');
    const [consumptionMethod, setConsumptionMethod] = useState('Flower');
    const [dateUsed, setDateUsed] = useState('');
    const [onsetMinutes, setOnsetMinutes] = useState(15);
    const [peakMinutes, setPeakMinutes] = useState(30);
    const [durationMinutes, setDurationMinutes] = useState(120);
    const [overallRating, setOverallRating] = useState(7);
    const [anxiety, setAnxiety] = useState(5);
    const [bodyHigh, setBodyHigh] = useState(5);
    const [headHigh, setHeadHigh] = useState(5);
    const [creativity, setCreativity] = useState(5);
    const [smoothness, setSmoothness] = useState(5);
    const [notes, setNotes] = useState('');

    useEffect(() => {
        fetchProducts();
        if (id) {
            fetchSessionData();
        }
    }, [id]);

    const fetchProducts = async () => {
        const { data } = await supabase
            .from('products')
            .select(`
                *,
                strain:strains (*),
                brand:brands (*)
            `)
            .order('label_name');
        if (data) setProducts(data as ProductDetails[]);
    };

    const fetchSessionData = async () => {
        try {
            setFetching(true);
            const { data, error: fetchError } = await supabase
                .from('sessions')
                .select('*')
                .eq('id', id)
                .single();

            if (fetchError) throw fetchError;
            if (data) {
                setProductId(data.product_id || '');
                setTotalPackagePrice(data.total_package_price || data.price_paid || 0);
                setTotalPackageWeight(data.total_package_weight || 3.5);
                setDosageAmount(data.dosage_amount || 0.5);
                setDosageUnit(data.dosage_unit || 'g');
                setTimeOfDay(data.time_of_day || 'Evening');
                setConsumptionMethod(data.consumption_method || 'Flower');
                setDateUsed(data.date_used);
                setOnsetMinutes(data.onset_minutes || 0);
                setPeakMinutes(data.peak_minutes || 0);
                setDurationMinutes(data.duration_minutes || 0);
                setOverallRating(data.overall_rating);
                setAnxiety(data.anxiety || 5);
                setBodyHigh(data.body_high || 5);
                setHeadHigh(data.head_high || 5);
                setCreativity(data.creativity || 5);
                setSmoothness(data.smoothness || 5);
                setNotes(data.notes || '');
            }
        } catch (err: any) {
            console.error('Error fetching session:', err);
            setError(err.message);
        } finally {
            setFetching(false);
        }
    };

    // Calculated fields
    const sessionCost = totalPackageWeight > 0 ? (totalPackagePrice / totalPackageWeight) * dosageAmount : 0;
    const valueScore = calculateValueScore(overallRating, durationMinutes, sessionCost);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !id) return;

        setLoading(true);
        setError(null);

        try {
            const { error: updateError } = await supabase
                .from('sessions')
                .update({
                    product_id: productId || null,
                    total_package_price: totalPackagePrice,
                    total_package_weight: totalPackageWeight,
                    dosage_amount: dosageAmount,
                    dosage_unit: dosageUnit,
                    session_cost: sessionCost,
                    time_of_day: timeOfDay,
                    consumption_method: consumptionMethod,
                    date_used: dateUsed,
                    onset_minutes: onsetMinutes,
                    peak_minutes: peakMinutes,
                    duration_minutes: durationMinutes,
                    overall_rating: overallRating,
                    anxiety,
                    body_high: bodyHigh,
                    head_high: headHigh,
                    creativity,
                    smoothness,
                    notes: notes || null,
                })
                .eq('id', id);

            if (updateError) throw updateError;
            navigate('/');
        } catch (err: any) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!id || !window.confirm('Are you sure you want to delete this session?')) return;

        setLoading(true);
        try {
            const { error: deleteError } = await supabase
                .from('sessions')
                .delete()
                .eq('id', id);

            if (deleteError) throw deleteError;
            navigate('/');
        } catch (err: any) {
            setError(err.message);
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <AppLayout title="Editing Session">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin text-accent-purple text-4xl">◌</div>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout title="Edit Session">
            <div className="max-w-2xl mx-auto mb-20">
                <form onSubmit={handleSubmit} className="space-y-8 pb-10">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/50 text-red-500 p-4 rounded-lg text-sm mb-6">
                            {error}
                        </div>
                    )}

                    {/* Product Selection */}
                    <div className="card space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">🌿</span>
                            <h3 className="font-semibold">Product Details</h3>
                        </div>

                        <div>
                            <SearchableSelect
                                label="Which product did you use?"
                                placeholder="Search products..."
                                options={products.map((p) => ({
                                    id: p.id,
                                    label: `${p.brand?.name} - ${p.label_name}`,
                                    subLabel: p.product_type
                                }))}
                                value={productId}
                                onChange={setProductId}
                            />
                        </div>

                        <div>
                            <label className="block text-sm text-dark-muted mb-2 font-medium">Consumption Method</label>
                            <select
                                value={consumptionMethod}
                                onChange={(e) => setConsumptionMethod(e.target.value)}
                                className="input-field bg-dark-bg cursor-pointer"
                            >
                                <option value="Bong">Bong</option>
                                <option value="Pipe">Pipe</option>
                                <option value="Joint">Joint</option>
                                <option value="Vape (Dry Herb)">Vape (Dry Herb) 💨</option>
                                <option value="Vape (Cart)">Vape (Cart)</option>
                                <option value="Dab">Dab</option>
                                <option value="Edible">Edible</option>
                            </select>
                        </div>
                    </div>

                    {/* Dose Calculator */}
                    <div className="card space-y-6 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                            <span className="text-6xl font-bold italic">$</span>
                        </div>

                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">⚖️</span>
                            <h3 className="font-semibold">Dose Calculator</h3>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs text-dark-muted mb-2 uppercase tracking-wider">Pack Price ($)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={totalPackagePrice}
                                    onChange={(e) => setTotalPackagePrice(Number(e.target.value))}
                                    className="input-field text-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-dark-muted mb-2 uppercase tracking-wider">Pack Size ({dosageUnit})</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={totalPackageWeight}
                                    onChange={(e) => setTotalPackageWeight(Number(e.target.value))}
                                    className="input-field text-lg"
                                />
                            </div>
                        </div>

                        <div className="pt-2">
                            <div className="flex justify-between items-end mb-3">
                                <label className="text-sm font-medium">Amount Used: <span className="text-accent-purple text-lg font-bold">{dosageAmount}</span> {dosageUnit}</label>
                                <div className="text-right">
                                    <span className="text-[10px] text-dark-muted block uppercase">Session Cost</span>
                                    <span className="text-xl font-bold text-accent-green">${sessionCost.toFixed(2)}</span>
                                </div>
                            </div>
                            <input
                                type="range"
                                min="0.05"
                                max={Math.max(2, totalPackageWeight)}
                                step="0.05"
                                value={dosageAmount}
                                onChange={(e) => setDosageAmount(Number(e.target.value))}
                                className="slider w-full h-3 mb-2"
                            />
                        </div>
                    </div>

                    {/* Experience Metrics */}
                    <div className="card space-y-6">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">✨</span>
                            <h3 className="font-semibold">Experience</h3>
                        </div>

                        <div>
                            <label className="block text-sm mb-4 font-medium">
                                Overall Rating: <span className="text-yellow-400 text-xl font-bold ml-1">★ {overallRating}/10</span>
                            </label>
                            <input
                                type="range"
                                min="1"
                                max="10"
                                step="1"
                                value={overallRating}
                                onChange={(e) => setOverallRating(Number(e.target.value))}
                                className="slider w-full h-8"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 pt-4 border-t border-dark-border/30">
                            <div>
                                <label className="block text-xs mb-2">Onset: <span className="text-accent-purple font-bold">{onsetMinutes} min</span></label>
                                <input
                                    type="range"
                                    min="0"
                                    max="60"
                                    step="5"
                                    value={onsetMinutes}
                                    onChange={(e) => setOnsetMinutes(Number(e.target.value))}
                                    className="slider w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-xs mb-2">Peak: <span className="text-accent-purple font-bold">{peakMinutes} min</span></label>
                                <input
                                    type="range"
                                    min="0"
                                    max="90"
                                    step="5"
                                    value={peakMinutes}
                                    onChange={(e) => setPeakMinutes(Number(e.target.value))}
                                    className="slider w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-xs mb-2">Duration: <span className="text-accent-purple font-bold">{durationMinutes} min</span></label>
                                <input
                                    type="range"
                                    min="0"
                                    max="360"
                                    step="15"
                                    value={durationMinutes}
                                    onChange={(e) => setDurationMinutes(Number(e.target.value))}
                                    className="slider w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-xs mb-2">Smoothness: <span className="text-accent-purple font-bold">{smoothness}/10</span></label>
                                <input
                                    type="range"
                                    min="1"
                                    max="10"
                                    step="1"
                                    value={smoothness}
                                    onChange={(e) => setSmoothness(Number(e.target.value))}
                                    className="slider w-full"
                                />
                            </div>
                        </div>
                    </div>

                    {/* More Details (Context) */}
                    <div className="card space-y-6">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">📅</span>
                            <h3 className="font-semibold">Context</h3>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm text-dark-muted mb-2 font-medium">Time of Day</label>
                                <select
                                    value={timeOfDay}
                                    onChange={(e) => setTimeOfDay(e.target.value)}
                                    className="input-field bg-dark-bg"
                                >
                                    <option value="Morning">Morning 🌅</option>
                                    <option value="Afternoon">Afternoon ☀️</option>
                                    <option value="Evening">Evening 🌆</option>
                                    <option value="Night">Night 🌙</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm text-dark-muted mb-2 font-medium">Date</label>
                                <input
                                    type="date"
                                    value={dateUsed}
                                    onChange={(e) => setDateUsed(e.target.value)}
                                    className="input-field bg-dark-bg"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm text-dark-muted mb-2 font-medium">Notes & Thoughts</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="input-field min-h-[120px] bg-dark-bg py-3"
                                placeholder="How did it feel? Any specific activities?"
                            />
                        </div>
                    </div>

                    <div className="flex gap-4 pt-4">
                        <button
                            type="button"
                            onClick={handleDelete}
                            className="bg-red-500/10 hover:bg-red-500/20 text-red-500 font-semibold px-6 py-4 rounded-xl transition-colors border border-red-500/20"
                        >
                            Delete
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary flex-1 text-lg py-4 shadow-xl shadow-accent-purple/20"
                        >
                            {loading ? 'Saving...' : `Update Session (Score: ${valueScore})`}
                        </button>
                    </div>
                </form>
            </div>
        </AppLayout>
    );
}
